import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/services/api";
import { Building2, Plus, Settings2, Loader2, FileText, DollarSign, Truck } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString("es-CO")}`;

const ESTADO_STYLE = {
  solicitado: "bg-gray-100 text-gray-700",
  aprobado: "bg-blue-100 text-blue-800",
  preparado: "bg-indigo-100 text-indigo-800",
  despachado: "bg-amber-100 text-amber-800",
  recibido: "bg-green-100 text-green-800",
  parcial: "bg-orange-100 text-orange-800",
  rechazado: "bg-red-100 text-red-700",
};

const SIGUIENTE_ACCION = {
  solicitado: [
    { estado: "aprobado", label: "Aprobar", clase: "bg-blue-700 hover:bg-blue-800" },
    { estado: "rechazado", label: "Rechazar", clase: "bg-red-600 hover:bg-red-700" },
  ],
  aprobado: [{ estado: "preparado", label: "Marcar Preparado", clase: "bg-indigo-700 hover:bg-indigo-800" }],
  preparado: [{ estado: "despachado", label: "Despachar", clase: "bg-amber-600 hover:bg-amber-700" }],
};

const EMPTY_FORM = {
  nombre: "",
  email: "",
  password: "",
  telefono: "",
  documento_id: "",
  direccion: "",
  descuento_tipo: "porcentaje",
  descuento_valor: 20,
  minimo_compra_unidades: 0,
  minimo_compra_valor: 0,
};

export default function AdminMayoristas() {
  const [tab, setTab] = useState("distribuidores");
  const [distribuidores, setDistribuidores] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCrear, setShowCrear] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pagando, setPagando] = useState(null);
  const [pago, setPago] = useState({ monto: "", metodo: "transferencia", referencia: "" });

  const load = useCallback(async () => {
    try {
      const [d, p, f] = await Promise.all([
        api.get("/mayoristas/admin/lista"),
        api.get("/mayoristas/admin/pedidos"),
        api.get("/mayoristas/admin/facturas"),
      ]);
      setDistribuidores(d.data);
      setPedidos(p.data);
      setFacturas(f.data);
    } catch (e) {
      toast.error("Error cargando datos de mayoristas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const crearDistribuidor = async () => {
    setSaving(true);
    try {
      await api.post("/mayoristas/admin/crear", {
        nombre: form.nombre,
        email: form.email,
        password: form.password,
        telefono: form.telefono,
        documento_id: form.documento_id,
        direccion: form.direccion,
        config_mayorista: {
          descuento_tipo: form.descuento_tipo,
          descuento_valor: parseFloat(form.descuento_valor) || 0,
          minimo_compra_unidades: parseInt(form.minimo_compra_unidades) || 0,
          minimo_compra_valor: parseFloat(form.minimo_compra_valor) || 0,
        },
      });
      toast.success("Distribuidor creado");
      setShowCrear(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear distribuidor");
    } finally {
      setSaving(false);
    }
  };

  const guardarConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/mayoristas/admin/${editando.id}/config`, {
        config_mayorista: {
          descuento_tipo: form.descuento_tipo,
          descuento_valor: parseFloat(form.descuento_valor) || 0,
          minimo_compra_unidades: parseInt(form.minimo_compra_unidades) || 0,
          minimo_compra_valor: parseFloat(form.minimo_compra_valor) || 0,
        },
      });
      toast.success("Configuración actualizada");
      setEditando(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const cambiarEstado = async (pedido, estado) => {
    try {
      await api.put(`/mayoristas/admin/pedidos/${pedido.id}/estado`, { estado });
      toast.success(`Pedido ${pedido.numero} → ${estado}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const facturar = async (pedido) => {
    try {
      const res = await api.post(`/mayoristas/admin/pedidos/${pedido.id}/facturar`);
      toast.success(`Factura ${res.data.numero} generada`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al facturar");
    }
  };

  const registrarPago = async () => {
    setSaving(true);
    try {
      await api.post(`/mayoristas/admin/facturas/${pagando.id}/pago`, {
        monto: parseFloat(pago.monto),
        metodo: pago.metodo,
        referencia: pago.referencia,
      });
      toast.success("Pago registrado");
      setPagando(null);
      setPago({ monto: "", metodo: "transferencia", referencia: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar pago");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A2C0A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-mayoristas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#4A2C0A] flex items-center gap-2">
            <Building2 className="w-6 h-6" /> Distribuidores Mayoristas
          </h1>
          <p className="text-sm text-gray-600 mt-1">Gestión de distribuidores, pedidos a bodega y facturación</p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowCrear(true); }} className="bg-[#4A2C0A]" data-testid="crear-distribuidor-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Distribuidor
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: "distribuidores", label: `Distribuidores (${distribuidores.length})`, icon: Building2 },
          { id: "pedidos", label: `Pedidos a Bodega (${pedidos.length})`, icon: Truck },
          { id: "facturas", label: `Facturas (${facturas.length})`, icon: FileText },
        ].map((t) => (
          <Button
            key={t.id}
            variant={tab === t.id ? "default" : "outline"}
            className={tab === t.id ? "bg-[#4A2C0A]" : ""}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-4 h-4 mr-2" /> {t.label}
          </Button>
        ))}
      </div>

      {tab === "distribuidores" && (
        <div className="bg-white rounded-2xl border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b">
                <th className="p-3">Distribuidor</th>
                <th className="p-3">Condición</th>
                <th className="p-3">Mínimos</th>
                <th className="p-3">Pedidos</th>
                <th className="p-3">Saldo</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {distribuidores.map((d) => (
                <tr key={d.id} className="border-b last:border-0 hover:bg-blue-50/30">
                  <td className="p-3">
                    <p className="font-medium text-[#4A2C0A]">{d.nombre}</p>
                    <p className="text-xs text-gray-500">{d.email}</p>
                  </td>
                  <td className="p-3">
                    {d.config_mayorista.descuento_tipo === "porcentaje"
                      ? `${d.config_mayorista.descuento_valor}% dcto`
                      : `${fmt(d.config_mayorista.descuento_valor)} dcto/und`}
                  </td>
                  <td className="p-3 text-xs">
                    {d.config_mayorista.minimo_compra_unidades > 0 && `${d.config_mayorista.minimo_compra_unidades} und `}
                    {d.config_mayorista.minimo_compra_valor > 0 && fmt(d.config_mayorista.minimo_compra_valor)}
                    {!d.config_mayorista.minimo_compra_unidades && !d.config_mayorista.minimo_compra_valor && "—"}
                  </td>
                  <td className="p-3">{d.pedidos_totales}</td>
                  <td className={`p-3 font-semibold ${d.saldo_pendiente > 0 ? "text-red-600" : "text-green-700"}`}>
                    {fmt(d.saldo_pendiente)}
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditando(d);
                        setForm({ ...EMPTY_FORM, ...d.config_mayorista });
                      }}
                      data-testid={`config-distribuidor-btn-${d.email}`}
                    >
                      <Settings2 className="w-4 h-4 mr-1" /> Configurar
                    </Button>
                  </td>
                </tr>
              ))}
              {distribuidores.length === 0 && (
                <tr><td colSpan="6" className="p-6 text-center text-gray-500">No hay distribuidores aún</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "pedidos" && (
        <div className="space-y-3">
          {pedidos.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border p-4" data-testid={`admin-pedido-${p.numero}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div>
                  <p className="font-bold text-[#4A2C0A]">{p.numero} — {p.distribuidor_nombre}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(p.fecha_creacion).toLocaleString("es-CO")} · {p.unidades} und · {fmt(p.total)}
                    {p.factura_numero && ` · ${p.factura_numero}`}
                  </p>
                </div>
                <Badge className={`${ESTADO_STYLE[p.estado]} border-0 capitalize`}>{p.estado}</Badge>
              </div>
              <div className="text-xs text-gray-600 mb-3">
                {p.items.map((i) => `${i.cantidad}× ${i.nombre}`).join(" · ")}
              </div>
              {p.notas && <p className="text-xs bg-amber-50 rounded p-2 mb-2">{p.notas}</p>}
              <div className="flex gap-2 flex-wrap">
                {(SIGUIENTE_ACCION[p.estado] || []).map((a) => (
                  <Button
                    key={a.estado}
                    size="sm"
                    className={a.clase}
                    onClick={() => cambiarEstado(p, a.estado)}
                    data-testid={`accion-${a.estado}-${p.numero}`}
                  >
                    {a.label}
                  </Button>
                ))}
                {!p.factura_id && !["solicitado", "rechazado"].includes(p.estado) && (
                  <Button size="sm" variant="outline" onClick={() => facturar(p)} data-testid={`facturar-btn-${p.numero}`}>
                    <FileText className="w-4 h-4 mr-1" /> Generar Factura
                  </Button>
                )}
              </div>
            </div>
          ))}
          {pedidos.length === 0 && <p className="text-center text-gray-500 py-8">No hay pedidos a bodega</p>}
        </div>
      )}

      {tab === "facturas" && (
        <div className="space-y-3">
          {facturas.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl border p-4" data-testid={`admin-factura-${f.numero}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-bold text-[#4A2C0A]">{f.numero} — {f.distribuidor_nombre}</p>
                  <p className="text-xs text-gray-500">
                    Pedido {f.pedido_numero} · {new Date(f.fecha_creacion).toLocaleDateString("es-CO")} · Total {fmt(f.total)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`border-0 capitalize ${f.estado === "pagada" ? "bg-green-100 text-green-800" : f.estado === "parcial" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>
                    {f.estado}
                  </Badge>
                  {f.saldo > 0 && (
                    <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => { setPagando(f); setPago({ monto: String(f.saldo), metodo: "transferencia", referencia: "" }); }} data-testid={`registrar-pago-btn-${f.numero}`}>
                      <DollarSign className="w-4 h-4 mr-1" /> Registrar Pago
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-sm mt-2">
                Saldo: <span className={`font-semibold ${f.saldo > 0 ? "text-red-600" : "text-green-700"}`}>{fmt(f.saldo)}</span>
                {f.pagos?.length > 0 && <span className="text-xs text-gray-500"> · {f.pagos.length} pago(s)</span>}
              </p>
            </div>
          ))}
          {facturas.length === 0 && <p className="text-center text-gray-500 py-8">No hay facturas generadas</p>}
        </div>
      )}

      {/* Crear distribuidor */}
      <Dialog open={showCrear} onOpenChange={setShowCrear}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Distribuidor Mayorista</DialogTitle>
            <DialogDescription>Crea el usuario y su condición comercial</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre / Razón Social *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="dist-nombre-input" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="dist-email-input" />
            </div>
            <div>
              <Label>Contraseña *</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="dist-password-input" />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            </div>
            <div>
              <Label>NIT / Documento</Label>
              <Input value={form.documento_id} onChange={(e) => setForm({ ...form, documento_id: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Dirección</Label>
              <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
            </div>
            <ConfigFields form={form} setForm={setForm} />
          </div>
          <Button onClick={crearDistribuidor} disabled={saving || !form.nombre || !form.email || !form.password} className="bg-[#4A2C0A] w-full" data-testid="crear-distribuidor-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crear Distribuidor
          </Button>
        </DialogContent>
      </Dialog>

      {/* Config distribuidor */}
      <Dialog open={!!editando} onOpenChange={(o) => !o && setEditando(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Condición Comercial — {editando?.nombre}</DialogTitle>
            <DialogDescription>{editando?.email}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <ConfigFields form={form} setForm={setForm} />
          </div>
          <Button onClick={guardarConfig} disabled={saving} className="bg-[#4A2C0A] w-full" data-testid="guardar-config-distribuidor">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
          </Button>
        </DialogContent>
      </Dialog>

      {/* Registrar pago */}
      <Dialog open={!!pagando} onOpenChange={(o) => !o && setPagando(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Registrar Pago — {pagando?.numero}</DialogTitle>
            <DialogDescription>Saldo pendiente: {fmt(pagando?.saldo)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Monto *</Label>
              <Input type="number" value={pago.monto} onChange={(e) => setPago({ ...pago, monto: e.target.value })} data-testid="pago-monto-input" />
            </div>
            <div>
              <Label>Método</Label>
              <Select value={pago.metodo} onValueChange={(v) => setPago({ ...pago, metodo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="nequi">Nequi</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referencia</Label>
              <Input value={pago.referencia} onChange={(e) => setPago({ ...pago, referencia: e.target.value })} />
            </div>
            <Button onClick={registrarPago} disabled={saving || !pago.monto} className="bg-green-700 hover:bg-green-800 w-full" data-testid="pago-submit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Registrar Pago
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ConfigFields = ({ form, setForm }) => (
  <>
    <div>
      <Label>Tipo de descuento</Label>
      <Select value={form.descuento_tipo} onValueChange={(v) => setForm({ ...form, descuento_tipo: v })}>
        <SelectTrigger data-testid="descuento-tipo-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="porcentaje">Porcentaje (%)</SelectItem>
          <SelectItem value="fijo">Valor fijo por unidad</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label>{form.descuento_tipo === "porcentaje" ? "Descuento (%)" : "Descuento ($/und)"}</Label>
      <Input type="number" value={form.descuento_valor} onChange={(e) => setForm({ ...form, descuento_valor: e.target.value })} data-testid="descuento-valor-input" />
    </div>
    <div>
      <Label>Mínimo compra (unidades)</Label>
      <Input type="number" value={form.minimo_compra_unidades} onChange={(e) => setForm({ ...form, minimo_compra_unidades: e.target.value })} data-testid="minimo-unidades-input" />
    </div>
    <div>
      <Label>Mínimo compra ($)</Label>
      <Input type="number" value={form.minimo_compra_valor} onChange={(e) => setForm({ ...form, minimo_compra_valor: e.target.value })} data-testid="minimo-valor-input" />
    </div>
  </>
);
